import React, { useState, useEffect } from 'react';
import { amDesignColors, amDesignTypography, amDesignComponents } from '../themes/amDesignSystem';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Publish as PublishIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as PreviewIcon,
  CompareArrows as CompareIcon,
  Schedule as PendingIcon,
  ArrowForward as ArrowIcon,
  InfoOutlined as InfoIcon
} from '@mui/icons-material';

const PendingChangesManager = ({ 
  open, 
  onClose, 
  locationId, 
  onPublishComplete 
}) => {
  const [pendingChanges, setPendingChanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishResults, setPublishResults] = useState(null);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [expandedChanges, setExpandedChanges] = useState(new Set());

  // Load pending changes when dialog opens
  useEffect(() => {
    if (open && locationId) {
      loadPendingChanges();
    }
  }, [open, locationId]);

  const loadPendingChanges = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/pending-changes/${locationId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setPendingChanges(data.changesSummary || []);
        setHasPendingChanges(data.hasPendingChanges);
        console.log('📋 Loaded pending changes:', data.changesSummary);
      } else {
        console.error('Failed to load pending changes:', data.error);
      }
    } catch (error) {
      console.error('Error loading pending changes:', error);
    }
    setLoading(false);
  };

  const handlePublishAll = async () => {
    if (!hasPendingChanges || pendingChanges.length === 0) return;
    
    setPublishing(true);
    setPublishResults(null);
    
    try {
      const response = await fetch(`/api/publish-changes/${locationId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      const result = await response.json();
      
      setPublishResults(result);
      
      if (result.success) {
        console.log('🚀 All changes published successfully');
        await loadPendingChanges();
        
        // Trigger global pending changes update
        window.dispatchEvent(new CustomEvent('pendingChangesUpdated'));
        
        if (onPublishComplete) {
          onPublishComplete(result);
        }
      } else {
        console.error('Publishing failed:', result.message);
        await loadPendingChanges();
      }
    } catch (error) {
      console.error('Error publishing changes:', error);
      setPublishResults({
        success: false,
        message: 'Fehler beim Veröffentlichen der Änderungen',
        error: error.message
      });
    }
    
    setPublishing(false);
  };

  const handleRemoveChange = async (field) => {
    try {
      const response = await fetch(`/api/pending-changes/${locationId}/${field}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        console.log('🗑️ Removed pending change:', field);
        await loadPendingChanges();
        
        // Trigger global pending changes update
        window.dispatchEvent(new CustomEvent('pendingChangesUpdated'));
      } else {
        console.error('Failed to remove change:', result.error);
      }
    } catch (error) {
      console.error('Error removing change:', error);
    }
  };

  const toggleExpanded = (changeId) => {
    const newExpanded = new Set(expandedChanges);
    if (newExpanded.has(changeId)) {
      newExpanded.delete(changeId);
    } else {
      newExpanded.add(changeId);
    }
    setExpandedChanges(newExpanded);
  };

  const formatValue = (value, isExpanded = false) => {
    if (value === null || value === undefined) return 'Nicht verfügbar';
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    
    const stringValue = value.toString();
    if (!isExpanded && stringValue.length > 120) {
      return stringValue.substring(0, 120) + '...';
    }
    return stringValue;
  };

  const getChangeTypeIcon = (type) => {
    switch (type) {
      case 'add': return <CheckIcon color="success" />;
      case 'delete': return <DeleteIcon color="error" />;
      case 'update':
      default: return <CompareIcon color="primary" />;
    }
  };

  const getFieldIcon = (field) => {
    const icons = {
      'description': '📝',
      'name': '🏢',
      'category': '🏷️',
      'phone': '📞',
      'website': '🌐',
      'address': '📍',
      'hours': '🕒',
      'attributes': '⚙️'
    };
    return icons[field] || '📄';
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xl" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          bgcolor: '#C41E3A',
          color: amDesignColors.white,
          py: 2,
          px: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PendingIcon sx={{ fontSize: 24 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', color: amDesignColors.white }}>
            Änderungen veröffentlichen
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Chip 
            label={`${pendingChanges.length} Änderung${pendingChanges.length !== 1 ? 'en' : ''}`}
            sx={{ 
              bgcolor: 'rgba(255,255,255,0.2)', 
              color: amDesignColors.white,
              fontWeight: 500,
              fontSize: '0.875rem',
              height: 28
            }}
          />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={60} />
          </Box>
        ) : !hasPendingChanges || pendingChanges.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <CheckIcon sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
            <Typography variant="h5" gutterBottom>
              Keine ausstehenden Änderungen
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Alle Änderungen sind bereits veröffentlicht oder es wurden noch keine Änderungen vorgenommen.
            </Typography>
          </Box>
        ) : (
          <Box>
            {/* Publishing Status */}
            {publishResults && (
              <Alert 
                severity={publishResults.success ? "success" : "error"} 
                sx={{ m: 3, borderRadius: 2 }}
                action={
                  <IconButton 
                    size="small" 
                    onClick={() => setPublishResults(null)}
                  >
                    <CloseIcon />
                  </IconButton>
                }
              >
                <Typography variant="body1" fontWeight="bold">
                  {publishResults.message}
                </Typography>
                {publishResults.note && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {publishResults.note}
                  </Typography>
                )}
              </Alert>
            )}

            {/* Publishing Progress */}
            {publishing && (
              <Box sx={{ p: 3, bgcolor: '#C41E3A', color: amDesignColors.white }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={20} sx={{ color: amDesignColors.white }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Veröffentliche Änderungen zu Google...
                  </Typography>
                </Box>
                <LinearProgress 
                  sx={{ 
                    mt: 2,
                    bgcolor: 'rgba(255,255,255,0.3)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: amDesignColors.white
                    }
                  }} 
                />
              </Box>
            )}

            {/* Changes Overview */}
            <Box sx={{ p: 3, bgcolor: '#f5f5f5', borderBottom: '1px solid amDesignColors.border' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                  Änderungsübersicht ({pendingChanges.length})
                </Typography>
                <Alert severity="info" sx={{ py: 0.5, px: 2, fontSize: '0.875rem' }}>
                  Änderungen werden zu Google gesendet und dort überprüft
                </Alert>
              </Box>
            </Box>

            {/* Changes List - Compact Table Style */}
            <Box sx={{ p: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', py: 1 }}>Feld</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', py: 1 }}>Aktuell</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', py: 1 }}>Neu</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', py: 1, textAlign: 'right' }}>Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingChanges.map((change, index) => {
                    const isExpanded = expandedChanges.has(change.field);
                    const hasLongContent = (change.oldValue?.length > 120) || (change.newValue?.length > 120);
                    
                    return (
                      <TableRow key={change.field} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                        <TableCell sx={{ py: 2, verticalAlign: 'top' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ fontSize: '1.2rem' }}>
                              {getFieldIcon(change.field)}
                            </Box>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                                {change.fieldLabel}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(change.timestamp).toLocaleString('de-DE', { 
                                  day: '2-digit', 
                                  month: '2-digit', 
                                  hour: '2-digit', 
                                  minute: '2-digit' 
                                })}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        
                        <TableCell sx={{ py: 2, maxWidth: 250, verticalAlign: 'top' }}>
                          <Box sx={{ 
                            p: 1.5, 
                            bgcolor: '#fff3e0', 
                            borderRadius: 1, 
                            border: '1px solid #ffb74d',
                            maxHeight: isExpanded ? 'none' : 80,
                            overflow: 'hidden'
                          }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontSize: '0.8rem',
                                lineHeight: 1.4,
                                color: '#1a1a1a',
                                wordBreak: 'break-word'
                              }}
                            >
                              {formatValue(change.oldValue, isExpanded)}
                            </Typography>
                          </Box>
                        </TableCell>
                        
                        <TableCell sx={{ py: 2, maxWidth: 250, verticalAlign: 'top' }}>
                          <Box sx={{ 
                            p: 1.5, 
                            bgcolor: '#e8f5e8', 
                            borderRadius: 1, 
                            border: '1px solid #66bb6a',
                            maxHeight: isExpanded ? 'none' : 80,
                            overflow: 'hidden'
                          }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontSize: '0.8rem',
                                lineHeight: 1.4,
                                color: '#1a1a1a',
                                wordBreak: 'break-word'
                              }}
                            >
                              {formatValue(change.newValue, isExpanded)}
                            </Typography>
                          </Box>
                        </TableCell>
                        
                        <TableCell sx={{ py: 2, textAlign: 'right', verticalAlign: 'top' }}>
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            {hasLongContent && (
                              <Tooltip title={isExpanded ? "Weniger" : "Mehr"}>
                                <IconButton 
                                  size="small"
                                  onClick={() => toggleExpanded(change.field)}
                                  sx={{ 
                                    bgcolor: '#f5f5f5',
                                    '&:hover': { bgcolor: amDesignColors.border }
                                  }}
                                >
                                  <PreviewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Entfernen">
                              <IconButton 
                                size="small"
                                onClick={() => handleRemoveChange(change.field)}
                                sx={{ 
                                  color: '#d32f2f',
                                  '&:hover': { bgcolor: '#ffebee' }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        p: 2, 
        bgcolor: '#f5f5f5', 
        borderTop: '1px solid amDesignColors.border',
        gap: 1,
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            onClick={onClose} 
            sx={{
              color: amDesignColors.text.secondary,
              fontWeight: 500,
              px: 2,
              py: 1,
              textTransform: 'none',
              '&:hover': { bgcolor: amDesignColors.border }
            }}
          >
            Schließen
          </Button>
          
          <Button 
            onClick={loadPendingChanges}
            startIcon={<RefreshIcon />}
            disabled={loading}
            sx={{
              color: amDesignColors.text.secondary,
              fontWeight: 500,
              px: 2,
              py: 1,
              textTransform: 'none',
              '&:hover': { bgcolor: amDesignColors.border }
            }}
          >
            Aktualisieren
          </Button>
        </Box>
        
        <Button 
          variant="contained"
          onClick={handlePublishAll}
          startIcon={publishing ? <CircularProgress size={16} color="inherit" /> : <PublishIcon />}
          disabled={!hasPendingChanges || pendingChanges.length === 0 || publishing}
          sx={{
            bgcolor: '#C41E3A',
            color: amDesignColors.white,
            fontWeight: 600,
            px: 3,
            py: 1,
            textTransform: 'none',
            '&:hover': { 
              bgcolor: '#a01729'
            },
            '&:disabled': {
              bgcolor: amDesignColors.border,
              color: amDesignColors.text.tertiary
            }
          }}
        >
          {publishing ? 'Veröffentliche...' : `${pendingChanges.length} Änderung${pendingChanges.length !== 1 ? 'en' : ''} veröffentlichen`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PendingChangesManager;